<template>
	<div class="hdl-pl-auth">
		<div class="hdl-auth-content">
			<div class="hdl-public-login mb-10" v-if="screen === 'login'">
				<div class="hdl-auth-container mb-5">
					<div class="hdl-heading-secondary text-center mb-2" v-if="board">
						<emoji
							:code-point="board.emoji"
							size="xl"
							class="mr-2"
							inline
							v-if="board.emoji"
						/>
						{{ board.name }}
					</div>
					<div class="mb-5 text-center" v-if="loginMethod === 'link'">
						Access the board with a magic link.
					</div>
					<div class="mb-5 text-center" v-else-if="loginMethod === 'password'">
						Login to your Medley account to access the board.
					</div>
					<banner
						hdl-style="red"
						class="my-3"
						v-if="!loggingIn && loginError && loginMethod === 'password'"
					>
						{{ loginError }}
					</banner>
					<div class="mb-5">
						<hdl-textbox
							v-model="email"
							placeholder="Enter your email"
							hdl-style="auth"
							class="w-full"
							ref="email"
							@enter="(loginMethod === 'link')? sendLink() : login()"
							focus-on-mount
						/>
						<hdl-textbox
							v-model="password"
							placeholder="Enter your password"
							hdl-style="auth"
							class="w-full mt-4"
							@enter="login"
							password
							v-if="loginMethod === 'password'"
						/>
					</div>
					<div class="flex flex-col items-center">
						<hdl-button
							@click.prevent="sendLink"
							label="Send magic link"
							hdl-style="primary"
							size="lg"
							class="w-full mb-5"
							:disabled="!emailIsValid"
							v-if="loginMethod === 'link'"
						/>
						<hdl-button
							@click.prevent="login"
							label="Log in"
							hdl-style="primary"
							size="lg"
							class="w-full mb-5"
							:is-loading="loggingIn"
							:disabled="!loginFormValid"
							v-else
						/>
						<div class="text-center" v-if="loginMethod === 'link'">
							Have a Medley account? <a href="#" @click.prevent="switchLoginMethod('password')">Log in</a> to access the board.
						</div>
						<div class="text-center" v-else-if="loginMethod === 'password'">
							Want to use a magic link instead?  <a href="#" @click.prevent="switchLoginMethod('link')">Log in with an email link</a> to access the board.
						</div>
						<!--<google-signin-button />-->
					</div>
				</div>
				<div class="hdl-footnote-text text-center">
					By accessing the board, you agree to the
					<a href="https://medley.so/company/terms-of-service" target="_blank">Terms of Service</a> and
					acknowledge the <a href="https://medley.so/company/privacy-policy" target="_blank">Privacy Policy</a>.
				</div>
			</div>
			<div class="hdl-auth-container mb-10" v-else-if="screen === 'link-sent'">
				<div class="text-center text-3xl mb-2">
					<font-awesome-icon
						:icon="['fad', 'envelope-circle-check']"
						style="--fa-primary-color: #010c18; --fa-secondary-color: #fbd828; --fa-secondary-opacity: 1; --fa-animation-iteration-count: 2;"
						bounce
					/>
				</div>
				<div class="hdl-heading-secondary text-center mb-5">
					Magic link sent
				</div>
				<div class="text-center mb-8">
					We've sent your magic login link to {{ email }}. If you're having trouble finding it, check your spam folder.
				</div>
				<div>
					<hdl-button
						@click.prevent="sendLink"
						label="Resend magic link"
						hdl-style="primary"
						:disabled="resendMagicLinkTimeout"
						size="lg"
						class="w-full mb-5"
					/>
					<hdl-button
						@click.prevent="screen = 'login'"
						label="Go back"
						hdl-style="secondary"
						size="lg"
						class="w-full"
					/>
				</div>
			</div>
			<div class="text-center">
				Made with
				<a href="https://www.medley.so" class="logo-link">
					<img class="inline medley-logo" src="/static/images/logo.svg" width="20" />
					Medley
				</a>
			</div>
	 	</div>
	</div>
</template>

<script lang="ts">
import { FetchResult, fetcher } from '@/common/fetcher';
import { Choice } from '@/common/types';
import { Vue, Component, Prop } from 'vue-facing-decorator'

import { library } from '@fortawesome/fontawesome-svg-core';
import { faEnvelopeCircleCheck } from '@fortawesome/pro-duotone-svg-icons';
import { BoardPublicType, BoardType } from '@/common/interfaces';
import Board from '@/app/components/boards/Board.vue';
import HdlTextbox from '@/components/common/HdlTextbox.vue';

library.add(faEnvelopeCircleCheck);

declare type LoginMethod = 'link' | 'password';
declare type LoginScreens = 'login' | 'link-sent';

const LoginChoices: Array<Choice<LoginMethod>> = [
	{
		label: 'Magic link',
		value: 'link'
	}, {
		label: 'Password',
		value: 'password'
	}
];

@Component
export default class PublicLogin extends Vue {
	loginChoices: Array<Choice<LoginMethod>> = LoginChoices;

	email: string = '';
	password: string = '';
	loginMethod: LoginMethod = 'link';

	screen: LoginScreens = 'login';

	resendMagicLinkTimeout: boolean = false;

	loggingIn: boolean = false;
	loginError: string | null = null;

	board: BoardPublicType | null = null;

	mounted(): void {
		const boardInitial = document.getElementById('board-initial');
		if (boardInitial !== null && boardInitial.textContent!) {
			const data = JSON.parse(boardInitial.textContent!) as BoardPublicType;
			if (data) {
				this.board = data;
			}
		}
	}

	switchLoginMethod(toMethod: LoginMethod): void {
		this.loginMethod = toMethod;

		this.$nextTick(() => {
			(this.$refs.email as HdlTextbox).focus();
		});
	}

	sendLink(): void {
		fetcher.Post(
			'/ac/l/',
			{
				method: 'link',
				email: this.email,
				board: this.$route.query.b
			}
		).then(
			(result: FetchResult) => {
				if (result.response.ok && result.json.success) {
					this.screen = 'link-sent';
					this.resendMagicLinkTimeout = true;

					setTimeout(() => {
						this.resendMagicLinkTimeout = false;
					}, 10000);
				}
			}
		);
	}

	login(): void {
		this.loggingIn = true;
		fetcher.Post(
			'/ac/l/',
			{
				method: 'password',
				email: this.email,
				board: this.$route.query.b,
				password: this.password
			}
		).then(
			(result: FetchResult) => {
				this.loginError = null;
				if (result.response.ok && result.json.success) {
					window.location.replace('/');
				} else if (result.response.ok && !result.json.success) {
					if (result.json.message) {
						this.loginError = result.json.message;
					}
				}
				this.loggingIn = false;
			}
		);
	}

	get emailIsValid(): boolean {
		return /(.+)@(.+){2,}\.(.+){2,}/.test(this.email);
	}

	get loginFormValid(): boolean {
		return (
			this.emailIsValid
			&& this.password.length > 0
		);
	}
}
</script>

<style lang="scss">
.hdl-pl-auth {
	@apply flex flex-row h-full w-full items-center justify-center bg-fill;

	.hdl-auth-content {
		width: 460px;
	}
}
</style>
